// The colour helpers of the customizer and the admin screens, out of CustomizerScreen
// (backfill PR 25) and utils.js, ColorsScreen, KeycapBaseLayer and KeycapColorEditor (PR 26):
// the colour picker's RGBA to the 8-digit hex the design stores; the grid and label colour
// that contrasts with a keycap's colour and the reddish check of a homing-bar label's keycap,
// which share one hex parser; the hue groups the colour catalogue is sorted into; the layout
// editor's keycap colours by row, group or colour group; and the colour a selection of
// keycaps shares most, with the random colours of the three colour groups.
export function rgbaToHex(color) {
  const { r, g, b, a } = color;

  const toHex = (value) => {
    const hex = Math.round(value).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }; // Helper function to convert a number to a 2-digit hex string

  // Convert RGB values to hex:
  const red = toHex(r);
  const green = toHex(g);
  const blue = toHex(b);

  const alpha = toHex(a * 255); // Convert the alpha value (0-1 range) to a 2-digit hex

  return `#${red}${green}${blue}${alpha}`; // Return the final 8-digit hex color code
} // this function allows me to use the edit the alpha value in the ChromePicker and store the color value as hex (ChromePicker doesn't allow for editing the alpha value using hex)

const hexToRgb = (hexColor) => ({
  r: parseInt(hexColor.slice(1, 3), 16),
  g: parseInt(hexColor.slice(3, 5), 16),
  b: parseInt(hexColor.slice(5, 7), 16),
}); // the channels of a #rrggbb colour: a keycap's colour, as the colour picker's six-digit hex writes it (rgbaToHex's 8-digit colours go to the texts and icons, never here)

export const getContrastingColor = (hexColor) => {
  const { r, g, b } = hexToRgb(hexColor); // Convert hex color to RGB

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255; // Calculate luminance

  return luminance > 0.5 ? 'black' : 'white'; // Return white for darker backgrounds, black for lighter
}; // this function determines the luminance of a color (color of the keycap) and returns the color of the grid that will be shown on the keycap

export const isRedishColor = (hexColor) => {
  if (!hexColor || hexColor[0] !== '#' || hexColor.length !== 7) return false;

  const { r, g, b } = hexToRgb(hexColor);

  const maxOther = Math.max(g, b);

  return r > 100 && r - maxOther > 40;
}; // this function checks if a hex color is a redish color ("Red-ish" = red is clearly dominant and reasonably strong)

export const hexToHSL = (H) => {
  let { r, g, b } = hexToRgb(H); // six digits: the catalogue holds no other value (checked by query, Oct 2026; GET is the colours' only route, so a document is inserted by hand). A value of another length would give NaN channels and crash sortColorsByGroups, where the deleted branches read it as black: no guard, user decision
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      // the maximum is one of the three channels
      // no default
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}; // the hue (0 to 360), saturation and lightness (0 to 100) of a #rrggbb colour

export const getHueGroup = (hue, saturation) => {
  if (saturation === 0) return 'Grey'; // black, white and the greys have no hue (hexToHSL gives them 0, which is red's)
  if ((hue >= 0 && hue < 15) || (hue >= 335 && hue <= 360)) return 'Red';
  if (hue >= 15 && hue < 45) return 'Orange';
  if (hue >= 45 && hue < 75) return 'Yellow';
  if (hue >= 75 && hue < 165) return 'Green';
  if (hue >= 165 && hue < 195) return 'Cyan';
  if (hue >= 195 && hue <= 240) return 'Blue'; // pure blue included
  if (hue > 240 && hue < 335) return 'Violet';
}; // the colour catalogue's group of a colour, by its hue unless it has no saturation

export const sortColorsByGroups = (colors) => {
  const groups = {
    Red: [],
    Orange: [],
    Yellow: [],
    Green: [],
    Cyan: [],
    Blue: [],
    Violet: [],
    Grey: [],
  };

  colors.forEach((colorObj) => {
    const hex = colorObj.hexValue;
    const { h, s, l } = hexToHSL(`#${hex}`);
    const group = getHueGroup(h, s);
    groups[group].push({ ...colorObj, l });
  });

  const sortedGroups = {};
  Object.keys(groups).forEach((group) => {
    sortedGroups[group] = groups[group].sort((a, b) => a.l - b.l);
  });

  return sortedGroups;
}; // the colour documents (GET /api/colors, hexValue without the #) in their hue groups and the greys, each group from dark to light, every group present even when empty; each colour gets its lightness as l

export const determineKeycapColor = (visibleGroup, colorGroup, group, row) => {
  if (visibleGroup === 'colorGroup') {
    if (colorGroup === 1) {
      return 'red';
    } else if (colorGroup === 2) {
      return 'blue';
    } else if (colorGroup === 3) {
      return 'green';
    }
  } else if (visibleGroup === 'group') {
    if (group === 'modifier') {
      return 'red';
    } else if (group === 'alphanumeric') {
      return 'blue';
    } else if (group === 'function') {
      return 'green';
    } else if (group === 'navigation') {
      return 'yellow';
    } else if (group === 'numpad') {
      return 'purple';
    }
  } else if (visibleGroup === 'row') {
    if (row === 'R1') {
      return '#780116';
    } else if (row === 'R1-1') {
      return '#780116';
    } else if (row === 'R1-2') {
      return '#780116';
    } else if (row === 'R2') {
      return '#C32F27';
    } else if (row === 'R2-3') {
      return '#C32F27';
    } else if (row === 'R3') {
      return '#DB7C26';
    } else if (row === 'R3-4') {
      return '#DB7C26';
    } else if (row === 'R4') {
      return '#F7B538';
    } else if (row === 'R4-4') {
      return '#F7B538';
    } else if (row === 'Uniform') {
      return '#1A1A1A';
    }
  }
};

export function pickMajorityKeycapColor(design, selectedKeycaps, fallback) {
  const getKeycap = (r, k) => design.keycapData?.[r]?.[k]; // helper function to get the keycap data based on a rowIndex and keycapIndex

  const actualSelected = selectedKeycaps
    .map(({ rowIndex, keycapIndex }) => getKeycap(rowIndex, keycapIndex))
    .filter((k) => k && !k.isSpacer); // make sure that all entries inside selectedKeycaps point to non-spacer objects (actual keycaps). Filter out all isSpacer object.

  if (actualSelected.length === 0) return fallback; // if selectedKeycaps is empty (or only holds entries that point to isSpacer objects), use the fallback color

  // Count color occurrences and remember first-seen order:
  const counts = new Map();
  const order = [];
  for (const k of actualSelected) {
    // go through all selected keycaps
    const col = k.color; // get the color of the current keycap (every keycap has one: the customizer writes the default white into each keycap at layout load and merges it into a stored design)

    if (!counts.has(col)) {
      // if the current color does not exist yet inside counts:
      counts.set(col, 0); // initialize the color inside counts
      order.push(col); // add the color to the order array
    }

    counts.set(col, counts.get(col) + 1); // increment the number for the current color inside counts
  }

  const max = Math.max(...counts.values()); // find the max frequency across counts
  const candidates = order.filter((col) => counts.get(col) === max); // create a new array from the order array, that only contains the colors that are present the most inside the selectedKeycaps. This array remembers the order in which the color candidates have been selected

  const last = selectedKeycaps[selectedKeycaps.length - 1]; // get the last selected keycap
  const lastColor = getKeycap(last.rowIndex, last.keycapIndex)?.color; // get the color of the last selected keycap

  if (lastColor && candidates.includes(lastColor)) return lastColor; // return the color of the last selected keycap if it is part of candidates

  return candidates[0]; // otherwise return the first candidate
} // Util: pick the color that appears most among selectedKeycaps, skipping spacers. (Tie-breaker: prefer the last selected keycap's color; otherwise first seen.)

export const generateRandomHexColor = () => {
  return `#${Math.floor(Math.random() * 16777215)
    .toString(16)
    .padStart(6, '0')}`;
}; // Generates a random hex color

export const COLOR_GROUPS = [1, 2, 3]; // the colour groups a layout assigns its keycaps to (colorGroup), the pattern the colour editor's Randomize button colours

export const applyColorGroupColors = (keycapData, colorGroupColors) =>
  keycapData.map((row) =>
    row.map((keycap) => {
      if (COLOR_GROUPS.includes(keycap.colorGroup)) {
        return {
          ...keycap,
          color: colorGroupColors[keycap.colorGroup], // Apply random color
          selectedKeycapColorIndex: null, // Unselect the color index
        };
      }
      return keycap; // Return spacers and other keycaps unchanged
    })
  ); // the keycaps of each colour group in that group's colour (colorGroupColors: a colour per group number), their colour-list reference cleared

export const nextColorGridIndex = (currentIndex, count, columnCount, key) => {
  let newIndex = currentIndex;
  switch (key) {
    case 'ArrowRight':
      newIndex = (currentIndex + 1) % count;
      break;
    case 'ArrowLeft':
      newIndex = (currentIndex - 1 + count) % count;
      break;
    case 'ArrowUp':
      newIndex = (currentIndex - columnCount + count) % count;
      break;
    case 'ArrowDown':
      newIndex = (currentIndex + columnCount) % count;
      if (newIndex >= count) newIndex = currentIndex % columnCount; // never true after the modulo above, so a step down from the last row wraps into another column (left as is, user decision, Oct 2026)
      break;
    default:
      return null;
  }
  return newIndex;
}; // the tile of the colour catalogue's grid an arrow key moves the selection to, wrapping around the ends (ColorsScreen, which reads the column count off the rendered grid; PR 32); null for any other key
